/**
 * Multi-select checklist modal for choosing which submissions to
 * include in the Planning View (A2F-450).
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { formatDateOnly } from '@/lib/dateOnly';
import type { Submission } from '@/types/api';

interface Props {
  submissions: Submission[];
  onApply: (selectedIds: string[]) => void;
  onClose: () => void;
}

export function SelectSubmissionsModal({ submissions, onApply, onClose }: Props) {
  const [checked, setChecked] = useState<Set<string>>(
    new Set(submissions.filter(s => s.isPlanning).map(s => s.id))
  );

  const toggle = (id: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApply = () => {
    onApply(Array.from(checked));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-brand-charcoal">Select Submissions</h2>
            <p className="text-xs text-slate-400 mt-0.5">Choose which submissions to track in the Planning View</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Checklist */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {[...submissions].reverse().map(sub => {
            // A2F-750: prefer the folder name (sub.notes) on the title
            // line for unambiguous picking on large IND projects. Trim
            // guards against ingest writing whitespace-only notes — a
            // theoretical tail risk that would otherwise render an
            // invisible title with no fallback. Empty/whitespace-only
            // notes fall back to submissionType (legacy / eCTD-parsed
            // projects where notes holds the eCTD submission_description
            // — also user-meaningful).
            const folderName = sub.notes?.trim() || '';
            const titleText = folderName || sub.submissionType || '';
            // Show submissionType on the secondary line only if it has a
            // value AND it's not the same string we just put on line 1
            // (avoids duplicating the fallback when notes is empty).
            const showTypeOnSecondary =
              folderName !== '' && !!sub.submissionType && sub.submissionType !== folderName;
            return (
              <label
                key={sub.id}
                className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked.has(sub.id)}
                  onChange={() => toggle(sub.id)}
                  className="w-4 h-4 rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30 accent-brand-orange"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-white bg-brand-orange px-1.5 py-0.5 rounded-full">
                      SUB {sub.displaySequence}
                    </span>
                    <span
                      className="text-sm font-medium text-brand-charcoal truncate"
                      title={titleText}
                    >
                      {titleText}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {showTypeOnSecondary && (
                      <>
                        <span className="truncate align-bottom inline-block max-w-[60%]">
                          {sub.submissionType}
                        </span>
                        {' '}&middot;{' '}
                      </>
                    )}
                    {sub.documentCount} doc{sub.documentCount !== 1 ? 's' : ''}
                    {sub.targetDate && (
                      <> &middot; Target: {formatDateOnly(sub.targetDate, { month: 'short', day: 'numeric' })}</>
                    )}
                  </p>
                </div>
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200">
          <span className="text-xs text-slate-400">{checked.size} selected</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700">
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-brand-orange/90 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
